import {
	ActionKind,
	OrderSampleAttachmentType,
	OrderSampleLifecycleStepEnum as SampleStepEnum,
	SampleActionActor,
	type GetOrderQuery,
} from '@codezo/graphql';
import {
	ActionBox,
	ActionContext,
	SampleRounds,
	type SampleRound,
	type SampleStep,
} from '@codezo/order-detail';
import { ExternalLink, FileText, Video } from 'lucide-react';
import { useState } from 'react';
import { Button } from '../../../components/ui/button';
import { FilePreview, type FilePreviewItem } from '../../../components/ui/file-preview';
import { SampleActionModal, type SampleAction } from '../../../components/orders/SampleActionModal';
import { formatDate } from '../../../lib/utils';

type OrderItem = NonNullable<GetOrderQuery['getOrder']>['items'][number];
type Sample = NonNullable<OrderItem['samples']>[number];
type SampleNextAction = NonNullable<Sample['availableActions']>[number];

/**
 * The live sample round — the last one. Earlier rounds are history: a rejected
 * round keeps its actions in the payload, and running one would move a round
 * that has already been replaced.
 */
export function liveSample(item: OrderItem): Sample | null {
	const rounds = item.samples ?? [];
	return rounds.length > 0 ? rounds[rounds.length - 1] : null;
}

/* `isForward`, `isEscape` and `openSampleActor` lived here — a local decoding of
 * which entries were real moves (status set, not cancel/reject, not
 * SET_PAYMENT_TREATMENT) and a rule that the round's turn was the first
 * unblocked one. All three are gone. `kind` says what an action is, and
 * `OrderItem.waitingOn` says whose turn it is. */

/**
 * When each sub-step happened.
 *
 * Read off the sample's own timestamps and matched by step id, never by
 * position: the backend words DELIVERY differently for a physical and a virtual
 * sample, and the date behind it is a different field too.
 */
function stepDate(sample: Sample, step: SampleStepEnum): string | null {
	switch (step) {
		case SampleStepEnum.Payment:
			return sample.paidAt ?? null;
		case SampleStepEnum.Production:
			return sample.productionStartedAt ?? null;
		case SampleStepEnum.Ready:
			return sample.productionCompletedAt ?? null;
		case SampleStepEnum.Delivery:
			return sample.shippedAt ?? sample.virtualDeliverySubmittedAt ?? null;
		case SampleStepEnum.Approved:
			return sample.approvedAt ?? null;
		default:
			return null;
	}
}

const roundState = (sample: Sample): SampleRound['state'] =>
	sample.approvedAt ? 'approved' : sample.rejectedAt ? 'declined' : 'current';

/**
 * A product's sampling, in the Sample & approval row.
 *
 * ── Why the row was empty before this ──
 *
 * The flow read its moves off `item.availableActions` — which, during sampling,
 * contains only moves that belong to LATER steps ("Start production", blocked
 * until payment) plus the permanent "Cancel item". So the row the product was
 * actually standing on offered nothing, while the studio's real next move sat
 * one level down in `samples[].availableActions`.
 *
 * ── Why it is a stepper and not a strip ──
 *
 * The first fix put the round's moves in an ActionBox: "Round 1 · In
 * production", three buttons beside it. That answers what can be done and
 * nothing else — not what came before, not what is left, not that this is the
 * second attempt. The design opens the row into the round's own six steps, and
 * `SampleRounds` draws them; this builds them.
 *
 * ── The mapping, and what it is not ──
 *
 * Every step, its label and its state come from `sample.lifecycle`. The moves
 * are placed by what they DO, off the payload's own fields:
 *
 * - a forward transition (`kind: MOVE`) goes on the step the round is standing
 *   on, because that is the step it moves off — and it is the only thing a step
 *   row carries;
 * - a property edit (`kind: PROPERTY`, SET_PAYMENT_TREATMENT) moves nothing, and
 *   an escape (`kind: ESCAPE`) ends the round rather than carrying it on. Both
 *   sit in the quiet row under the stepper. The item's own cancel follows the
 *   same rule on the product card.
 *
 * Nothing here decides whether a move is possible: `blocked` and
 * `blockedReason` are the server's, and a blocked action renders disabled
 * carrying its own reason rather than disappearing.
 */
export function SampleRoundActions({
	item,
	onChanged,
}: {
	item: OrderItem;
	onChanged: () => void;
}) {
	const [action, setAction] = useState<{ sample: Sample; action: SampleAction } | null>(null);
	const [preview, setPreview] = useState<{ files: FilePreviewItem[]; index: number } | null>(
		null
	);

	const samples = item.samples ?? [];
	if (samples.length === 0) return null;

	/**
	 * One control, at one of two weights.
	 *
	 * `xs` rather than `sm`: these sit on a 12px sub-stepper, and a 28px control
	 * made every row carrying one half again taller than its neighbours — which is
	 * most of why the stepper read as uneven rather than as one list.
	 *
	 * Ghost is for the quiet row at the foot only. It draws no border until hover,
	 * so next to an outline button it reads as stray text rather than as a
	 * control; that is fine where nothing else competes and wrong on a step row.
	 */
	const button = (sample: Sample, a: SampleNextAction, quiet?: boolean) => (
		<Button
			// Not the status alone: a property action carries none.
			key={`${a.action}:${a.status ?? ''}`}
			size="xs"
			variant={quiet ? 'ghost' : 'outline'}
			disabled={!!a.blocked}
			title={a.blocked ? (a.blockedReason ?? undefined) : undefined}
			onClick={() => setAction({ sample, action: a as SampleAction })}
		>
			{a.label}
		</Button>
	);

	/**
	 * A row of file tiles, opening the app's own preview.
	 *
	 * One helper rather than one copy per list: the round now has two — what the
	 * studio sent and what the client sent back with a decline — and they must
	 * look identical, because the only thing separating them is where they sit.
	 */
	const strip = (files: FilePreviewItem[]) =>
		files.length > 0 ? (
			<div className="mt-2.5 ml-[22px] flex flex-wrap gap-2">
				{files.map((file, i) => (
					<button
						key={file.id}
						type="button"
						onClick={() => setPreview({ files, index: i })}
						className="flex items-center gap-1.5 rounded-md bg-surface-fill px-2.5 py-1.5 text-xs text-text-2 transition-colors hover:text-text-1"
					>
						{file.mimeType.toLowerCase().startsWith('image/') ? (
							<img src={file.url} alt="" className="h-6 w-6 rounded object-cover" />
						) : file.mimeType.toLowerCase().startsWith('video/') ? (
							<Video size={12} className="text-text-4" />
						) : (
							<FileText size={12} className="text-text-4" />
						)}
						{file.name}
					</button>
				))}
			</div>
		) : undefined;

	const rounds = samples.map<SampleRound>((sample) => {
		const actions = (sample.availableActions ?? []).filter(
			(a) => a.actor === SampleActionActor.Admin
		);
		// Split by what the server says each action IS, not by decoding its status.
		const forward = actions.filter((a) => a.kind === ActionKind.Move);
		/**
		 * Moves with no step to stand on.
		 *
		 * A step row carries the move that leaves it, which works while the round
		 * is standing somewhere. A DECLINED round is standing nowhere — no step is
		 * `current` — so the studio's answer to the decline (greenlighting the next
		 * round) was filtered out of every row and rendered nowhere at all. The
		 * client declined and the page offered the studio nothing.
		 *
		 * Same shape as "Start production" having no home on the product spine, and
		 * the same fix: a move the server is offering gets drawn, and if no row owns
		 * it, the foot does.
		 */
		/**
		 * Whether the STEPPER is on screen to hold a move.
		 *
		 * Two ways it isn't. The round may be standing on no step at all — nothing
		 * `current` — which is what this originally covered. Or the round may be
		 * DECLINED, and then `SampleRounds` draws the record of the decline in place
		 * of the run: the reason, the client's files, and no step rows. APPROVAL is
		 * still `current` on a declined round (nothing has been approved, and the
		 * actor has turned round to the studio), so the first test passes and the
		 * move was handed to a step row that is never rendered — the button existed,
		 * in a part of the page that does not exist.
		 */
		const stepperDrawn = !sample.rejectedAt && (sample.lifecycle ?? []).some((s) => s.current);
		const homeless = stepperDrawn ? [] : forward;
		const escapes = actions.filter((a) => a.kind === ActionKind.Escape);
		const treatment = actions.filter((a) => a.kind === ActionKind.Property);
		// The other party's turn — the round is sitting on the client, and the row
		// should say so rather than go quiet.
		const awaitingClient = (sample.availableActions ?? []).some(
			(a) => a.actor === SampleActionActor.Client && !a.blocked
		);

		// The terminal step is drawn only once it is TRUE.
		//
		// While a round is running, "Approved" as a sub-step would be a dot
		// claiming to be the round's position at the moment it has none, sitting
		// directly under a step already reading "Awaiting review". So it is hidden
		// — and it used to be hidden always, which is how an APPROVED round ended
		// on "Awaiting review" wearing a tick: the stepper said the round was
		// waiting for something that had already happened, and the step that would
		// have said so was the one being filtered out.
		//
		// Once `approvedAt` is set it is the answer, not a claim about the future.
		const lifecycle = (sample.lifecycle ?? []).filter(
			(s) => s.step !== SampleStepEnum.Approved || !!sample.approvedAt
		);
		const steps = lifecycle.map<SampleStep>((step) => {
			const active = step.current;
			const at = stepDate(sample, step.step);
			// The step's OWN move, and nothing else.
			//
			// SET_PAYMENT_TREATMENT used to ride the Payment row on the argument that
			// it is about that step. It is `kind: PROPERTY` — it moves nothing — and
			// putting it here meant a finished row carried a control while the active
			// row carried a different one at a different weight, with a date floating
			// between them. Properties and escapes now share the quiet row at the
			// foot, so the stepper is dots, labels, dates, and exactly one control on
			// the step the round is standing on.
			const own = active && stepperDrawn ? forward : [];
			// A blocked forward move already carries the server's sentence on its
			// own button; this repeats it in the open where a disabled button's
			// title cannot be read on touch. By reason, not by action: one gate
			// usually blocks several.
			const reason = own.find((a) => a.blocked && a.blockedReason)?.blockedReason;

			return {
				key: step.step,
				label: step.label,
				state: step.completed
					? 'done'
					: active
						? 'active'
						: step.outstanding
							? 'outstanding'
							: 'upcoming',
				at: step.completed && at ? formatDate(at) : undefined,
				action:
					own.length > 0 ? (
						<span className="inline-flex gap-1.5">
							{own.map((a) => button(sample, a))}
						</span>
					) : undefined,
				// The blocked move's reason while the round stands here; the server's
				// outstanding sentence when it has moved past a step it never did.
				note:
					(active && reason ? reason : undefined) ?? step.outstandingReason ?? undefined,
			};
		});

		/**
		 * The round's files, split by WHO sent them.
		 *
		 * `attachments` is one list carrying both kinds and the backend types every
		 * entry (REJECTION / VIRTUAL_DELIVERY). We selected `type` and then ignored
		 * it, so the client's photographs of what was wrong sat in the same strip as
		 * the studio's photographs of the sample — the objection filed under the
		 * thing objected to. The client's own page has kept them apart all along, in
		 * two separate fields.
		 */
		const toItems = (list: NonNullable<Sample['attachments']>): FilePreviewItem[] =>
			list
				.filter((a) => a.url)
				.map((a) => ({
					id: a.id,
					url: a.url,
					name: a.name,
					mimeType: a.mimeType,
					size: a.size,
				}));

		const attachments = sample.attachments ?? [];
		const files = toItems(
			attachments.filter((a) => a.type !== OrderSampleAttachmentType.Rejection)
		);
		const declineFiles = toItems(
			attachments.filter((a) => a.type === OrderSampleAttachmentType.Rejection)
		);

		const tracking = [sample.carrier, sample.trackingNumber].filter(Boolean).join(' ');

		return {
			id: sample.id,
			round: sample.sampleRound,
			state: roundState(sample),
			steps,
			media: strip(files),
			note: sample.virtualDeliveryNotes || undefined,
			declinedAt: sample.rejectedAt ? formatDate(sample.rejectedAt) : undefined,
			declineReason: sample.rejectionReason,
			declineMedia: strip(declineFiles),
			// The `action` slot the package reserved for exactly this and nothing
			// ever filled. `trackingUrl` is resolved server-side from the shared
			// carrier list and is null when the carrier is unrecognised or has no
			// tracking page — so the button appears only when there is somewhere
			// real to go, and the number stays readable on its own when there is
			// not. Never built from the carrier name here: a guessed link that
			// 404s is worse than no link.
			tracking: tracking
				? {
						label: <span className="font-mono text-text-1">{tracking}</span>,
						action: sample.trackingUrl ? (
							<Button
								size="xs"
								variant="outline"
								onClick={() =>
									window.open(sample.trackingUrl!, '_blank', 'noopener')
								}
							>
								Track
								<ExternalLink size={12} data-icon="inline-end" />
							</Button>
						) : undefined,
					}
				: undefined,
			footer: (
				<>
					{awaitingClient && (
						<ActionBox>
							<ActionContext>Waiting on the client&rsquo;s review</ActionContext>
						</ActionBox>
					)}
					{/* The move the stepper could not place — see `homeless`. Full
					    weight, not the quiet row's ghost: this is the round's next
					    move, not an aside about it. */}
					{homeless.length > 0 && (
						<ActionBox mine>
							<ActionContext>
								{sample.rejectedAt
									? 'The client declined this round.'
									: 'This round is not standing on a step.'}
							</ActionContext>
							<span className="inline-flex gap-1.5">
								{homeless.map((a) => button(sample, a))}
							</span>
						</ActionBox>
					)}
					{/* Everything that is not a step move, in one quiet row: property
					    edits on the left, the way out pushed to the right. Ruled off so
					    it reads as belonging to the round rather than as a seventh step,
					    and indented to the stepper's own text column (12px rail + 10px
					    gap). */}
					{(treatment.length > 0 || escapes.length > 0) && (
						<div className="mt-3 ml-[22px] flex flex-wrap items-center gap-1.5 border-t border-line-2 pt-2.5">
							{treatment.map((a) => button(sample, a, true))}
							{treatment.length > 0 && escapes.length > 0 && (
								<span className="flex-1" />
							)}
							{escapes.map((a) => button(sample, a, true))}
						</div>
					)}
				</>
			),
		};
	});

	return (
		<>
			<SampleRounds rounds={rounds} />

			{action && (
				<SampleActionModal
					open
					onOpenChange={(open) => {
						if (!open) setAction(null);
					}}
					action={action.action}
					sampleId={action.sample.id}
					sampleChoice={action.sample.sampleChoice}
					sampleRound={action.sample.sampleRound}
					productName={item.productName || 'this item'}
					paymentTreatment={action.sample.paymentTreatment}
					onSuccess={() => {
						setAction(null);
						onChanged();
					}}
				/>
			)}

			{preview && (
				<FilePreview
					files={preview.files}
					initialIndex={preview.index}
					onClose={() => setPreview(null)}
				/>
			)}
		</>
	);
}
